import React, { useState, useEffect } from 'react';

const TRACKS_URL = 'https://n293vk0zm6.execute-api.us-east-1.amazonaws.com/prod/tracks';

function fmt(n) {
  if (!n && n !== 0) return '—';
  const num = typeof n === 'string' ? parseFloat(n.replace(/,/g, '')) : n;
  if (isNaN(num)) return String(n);
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000)    return (num / 1000).toFixed(0) + 'K';
  return String(num);
}

function modelsLabel(alert) {
  const mf = alert.models_fired || '';
  if (mf === 'isolation_forest+kmeans') return 'Isolation Forest + K-Means';
  if (mf === 'isolation_forest')        return 'Isolation Forest only';
  if (mf === 'kmeans')                  return 'K-Means only';
  if (alert.both_models)                return 'Isolation Forest + K-Means';
  return 'Isolation Forest only';
}

function addDays(dateStr, days) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function daysBetween(dateA, dateB) {
  const a = new Date(dateA + 'T00:00:00');
  const b = new Date(dateB + 'T00:00:00');
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

function buildSegments(history, deltas) {
  // Returns array of { type: 'bar'|'gap', ... }
  const segments = [];
  for (let i = 0; i < history.length; i++) {
    segments.push({ type: 'bar', entry: history[i], delta: deltas[i], index: i });
    if (i < history.length - 1) {
      const diff = daysBetween(history[i].date, history[i + 1].date);
      if (diff > 1) {
        const gapStart = addDays(history[i].date, 1);
        const gapEnd   = addDays(history[i + 1].date, -1);
        segments.push({ type: 'gap', days: diff - 1, gapStart, gapEnd });
      }
    }
  }
  return segments;
}

function TrendChart({ history }) {
  const [tooltip, setTooltip] = useState(null);

  const deltas = history.map((d, i) =>
    i === 0 ? 0 : Math.max(0, d.playcount - history[i - 1].playcount)
  );

  const displayHistory = history.slice(1);
  const displayDeltas  = deltas.slice(1);
  const max            = Math.max(...displayDeltas, 1);
  const segments       = buildSegments(displayHistory, displayDeltas);
  const hasGap         = segments.some(s => s.type === 'gap');

  // Compute gap labels for summary
  const gapSegments = segments.filter(s => s.type === 'gap');
  const gapNote = gapSegments.map(g =>
    `${formatDate(g.gapStart)}–${formatDate(g.gapEnd)}`
  ).join(', ');

  // Total flex units for position calculation
  const totalFlex = segments.reduce((sum, s) =>
    sum + (s.type === 'bar' ? 1 : s.days), 0
  );

  let barIndex = 0;

  return (
    <div style={{ position: 'relative' }}>
      {tooltip && (
        <div style={{
          position: 'absolute', top: '-80px',
          left: `${Math.max(5, Math.min(tooltip.x, 65))}%`,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: '8px', padding: '8px 10px', fontSize: '11px',
          color: 'var(--text)', zIndex: 10, pointerEvents: 'none',
          minWidth: '130px', transform: 'translateX(-50%)'
        }}>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>{tooltip.date}</div>
          <div style={{ color: 'var(--muted)', marginBottom: '2px' }}>
            Daily plays: <span style={{ color: 'var(--text)', fontWeight: 500 }}>
              {tooltip.delta > 0 ? '+' + fmt(tooltip.delta) : '—'}
            </span>
          </div>
          <div style={{ color: 'var(--muted)', marginBottom: '2px' }}>
            Total: <span style={{ color: 'var(--text)', fontWeight: 500 }}>{fmt(tooltip.playcount)}</span>
          </div>
          <div style={{ color: 'var(--muted)' }}>
            Rank: <span style={{ color: 'var(--text)', fontWeight: 500 }}>#{tooltip.rank}</span>
          </div>
          {tooltip.flagged && (
            <div style={{ color: 'var(--amber)', marginTop: '4px', fontWeight: 500 }}>Flagged here</div>
          )}
        </div>
      )}

      <div style={{
        height: '90px', background: 'var(--bg)', borderRadius: '8px',
        padding: '8px 12px', display: 'flex', alignItems: 'flex-end',
        gap: '3px', border: '1px solid var(--border)', position: 'relative'
      }}>
        {segments.map((seg, si) => {
          if (seg.type === 'gap') {
            return (
              <div
                key={`gap-${si}`}
                style={{
                  flex: seg.days, height: '70%', alignSelf: 'center',
                  border: '1px dashed var(--border)', borderRadius: '4px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 2px', overflow: 'hidden'
                }}
              >
                <span style={{
                  fontSize: '8px', color: 'var(--muted)',
                  writingMode: 'vertical-lr', transform: 'rotate(180deg)',
                  letterSpacing: '0.5px', whiteSpace: 'nowrap'
                }}>
                  off chart
                </span>
              </div>
            );
          }

          const { entry, delta } = seg;
          const h         = Math.max(4, Math.round((delta / max) * 100));
          const isLast    = seg.index === displayHistory.length - 1;
          const isFlagged = entry.flagged;
          const pct       = Math.max(5, Math.min(
            Math.round((barIndex / Math.max(displayHistory.length - 1, 1)) * 100),
            65
          ));
          barIndex++;

          return (
            <div
              key={`bar-${si}`}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', cursor: 'pointer' }}
              onMouseEnter={() => setTooltip({ ...entry, delta, x: pct })}
              onMouseLeave={() => setTooltip(null)}
            >
              {isFlagged && (
                <div style={{ fontSize: '9px', color: 'var(--amber)', fontWeight: 500, marginBottom: '2px', whiteSpace: 'nowrap' }}>
                  flagged
                </div>
              )}
              <div style={{
                width: '100%', borderRadius: '2px 2px 0 0',
                minHeight: '4px', height: `${h}%`,
                background: isFlagged ? 'var(--amber)' : isLast ? 'var(--accent)' : 'var(--bar-normal)',
                opacity: tooltip && tooltip.date !== entry.date ? 0.6 : 1,
                transition: 'opacity 0.1s',
                animation: `growBar 0.5s ease forwards`,
                animationDelay: `${si * 40}ms`,
                transform: 'scaleY(0)',
                transformOrigin: 'bottom'
              }} />
            </div>
          );
        })}
      </div>

      {/* Date labels */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--muted)', marginTop: '4px' }}>
        <span>{formatDate(displayHistory[0]?.date)}</span>
        <span>{formatDate(displayHistory[displayHistory.length - 1]?.date)}</span>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
        {[
          { color: 'var(--bar-normal)',      label: 'Normal day',  border: false },
          { color: 'var(--amber)', label: 'Flagged day', border: false },
          { color: 'var(--accent)',label: 'Today',       border: false },
          ...(hasGap ? [{ color: 'transparent', label: 'Off chart', border: true }] : []),
        ].map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{
              width: '8px', height: '8px', borderRadius: '2px',
              background: l.color, flexShrink: 0,
              border: l.border ? '1px dashed var(--muted)' : 'none'
            }} />
            <span style={{ fontSize: '10px', color: 'var(--muted)' }}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Gap note */}
      {hasGap && (
        <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '6px', fontStyle: 'italic' }}>
          Track left Top 50: {gapNote}
        </div>
      )}
    </div>
  );
}

export default function AlertModal({ alert, alerts, allAlerts = [], onClose }) {
  console.log('allAlerts count:', allAlerts.length, 'current track_id:', alert?.track_id);
  const trackHistory = allAlerts.filter(a =>
    a.track_id?.toLowerCase() === alert?.track_id?.toLowerCase()
  );
  console.log('trackHistory for this track:', trackHistory);

  const [history, setHistory]         = useState([]);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => {
    if (!alert?.track_id) return;
    setHistory([]);
    setHistLoading(true);
    fetch(`${TRACKS_URL}?track_id=${encodeURIComponent(alert.track_id)}`)
      .then(r => r.json())
      .then(data => {
        const flagSource = allAlerts.length > 0 ? allAlerts : (alerts || []);
        const flaggedDates = new Set(
          flagSource
            .filter(a => a.track_id?.toLowerCase() === alert.track_id?.toLowerCase())
            .map(a => a.detected_date)
        );
        const enriched = (data.history || []).map(d => ({
          ...d,
          flagged: flaggedDates.has(d.date)
        }));
        setHistory(enriched);
      })
      .catch(() => setHistory([]))
      .finally(() => setHistLoading(false));
  }, [alert?.track_id]);

  if (!alert) return null;

  const isHigh = alert.severity === 'high';
  const pct    = alert.anomaly_score ? Math.round(parseFloat(alert.anomaly_score) * 100) : null;
  const models = modelsLabel(alert);

  const cooldownExpires = alert.detected_date ? formatDate(addDays(alert.detected_date, 5)) : '—';

  const historySource = allAlerts.length > 0 ? allAlerts : (alerts || []);
  const allForTrack   = historySource.filter(a => a.track_id?.toLowerCase() === alert.track_id?.toLowerCase());
  const firstFlagged  = allForTrack.length > 0
    ? formatDate(allForTrack.map(a => a.detected_date).sort()[0])
    : formatDate(alert.detected_date);

  const scoreHistory = allForTrack
    .sort((a, b) => a.detected_date.localeCompare(b.detected_date))
    .map(a => ({
      date:  formatDate(a.detected_date),
      score: Math.round(parseFloat(a.anomaly_score) * 100)
    }));

  const displayHistory = history.slice(1);
  const trendSummary = displayHistory.length >= 2 ? (() => {
    const first     = displayHistory[0].playcount;
    const last      = displayHistory[displayHistory.length - 1].playcount;
    const growth    = Math.round(((last - first) / first) * 100);
    const startDate = formatDate(displayHistory[0].date);
    const endDate   = formatDate(displayHistory[displayHistory.length - 1].date);
    const firstRank = displayHistory[0].rank;
    const lastRank  = displayHistory[displayHistory.length - 1].rank;
    return { growth, firstRank, lastRank, startDate, endDate };
  })() : null;

  const chipStyle = {
    background: 'var(--bg)', borderRadius: '8px',
    padding: '10px 12px', border: '1px solid var(--border)'
  };
  const chipLabel = {
    fontSize: '10px', color: 'var(--muted)',
    textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '3px'
  };
  const chipVal = { fontSize: '16px', fontWeight: 500, color: 'var(--text)' };

  return (
    <div
      className="alert-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '24px 16px', animation: 'modalFadeOverlay 0.2s ease'
      }}
    >
      <div
        className="alert-modal-box"
        style={{
          background: 'var(--surface)', borderRadius: '12px', padding: '20px',
          width: '100%', maxWidth: '480px', border: '1px solid var(--border)',
          maxHeight: '90vh', overflowY: 'auto',
          animation: 'modalSpringIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 500, color: 'var(--text)' }}>{alert.name}</div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '2px' }}>{alert.artist}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: 'var(--muted)', cursor: 'pointer', lineHeight: 1 }}>✕</button>
        </div>

        {/* First flagged + cooldown */}
        <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--muted)', marginBottom: '14px' }}>
          <span>First flagged <span style={{ color: 'var(--text)', fontWeight: 500 }}>{firstFlagged}</span></span>
          <span>Cooldown expires <span style={{ color: 'var(--text)', fontWeight: 500 }}>{cooldownExpires}</span></span>
        </div>

        {/* 4 metric chips */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '14px' }}>
          <div style={chipStyle}>
            <div style={chipLabel}>Anomaly score</div>
            <div style={{ ...chipVal, color: pct ? 'var(--accent)' : 'var(--muted)' }}>
              {pct ? `${pct}%` : '—'}
            </div>
          </div>
          <div style={chipStyle}>
            <div style={chipLabel}>Rank when flagged</div>
            <div style={{ ...chipVal }}>#{alert.rank}</div>
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '2px' }}>
              {alert.detected_date ? formatDate(alert.detected_date) : ''}
            </div>
          </div>
          <div style={chipStyle}>
            <div style={chipLabel}>Rank now</div>
            {alert.rank_now ? (
              <div style={{ ...chipVal, color: alert.rank - alert.rank_now > 0 ? 'var(--accent)' : alert.rank - alert.rank_now < 0 ? 'var(--amber)' : 'var(--text)' }}>
                #{alert.rank_now}
                {alert.rank - alert.rank_now !== 0 && (
                  <span style={{ fontSize: '12px', marginLeft: '6px' }}>
                    {alert.rank - alert.rank_now > 0 ? `↑ ${alert.rank - alert.rank_now}` : `↓ ${Math.abs(alert.rank - alert.rank_now)}`} spots
                  </span>
                )}
              </div>
            ) : (
              <div style={{ ...chipVal, color: 'var(--muted)' }}>—</div>
            )}
            <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '2px' }}>
              As of today
            </div>
          </div>
          <div style={chipStyle}>
            <div style={chipLabel}>Listeners</div>
            <div style={chipVal}>{fmt(alert.listeners)}</div>
          </div>
        </div>

        {/* Anomaly detail */}
        {pct && (
          <div style={{
            background: isHigh ? 'var(--accent-bg)' : 'var(--amber-bg)',
            border: `1px solid ${isHigh ? 'var(--accent-bd)' : 'var(--amber-bd)'}`,
            borderRadius: '8px', padding: '12px', marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '11px', color: 'var(--sub)' }}>Models fired</span>
              <span style={{ fontSize: '12px', fontWeight: 500, color: isHigh ? 'var(--accent)' : 'var(--muted)' }}>{models}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '11px', color: 'var(--sub)' }}>Severity</span>
              <span style={{ fontSize: '12px', fontWeight: 500, color: isHigh ? 'var(--accent)' : 'var(--amber)' }}>
                {isHigh ? 'High' : 'Medium'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--muted)', marginBottom: '3px' }}>
              <span>Anomaly score</span>
              <span style={{ fontWeight: 500, color: isHigh ? 'var(--accent)' : 'var(--amber)' }}>{pct}%</span>
            </div>
            <div style={{ height: '4px', background: 'var(--border)', borderRadius: '2px', overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: '2px', background: isHigh ? 'var(--accent)' : 'var(--amber)' }} />
            </div>
          </div>
        )}

        {/* 14-day trend chart */}
        <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '6px' }}>
          14-day playcount trend
        </div>
        {histLoading && (
          <div style={{ fontSize: '12px', color: 'var(--muted)', padding: '10px 12px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '12px' }}>
            Loading trend data...
          </div>
        )}
        {!histLoading && history.length > 0 && (
          <>
            <TrendChart history={history} />
            {trendSummary && (
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px', marginBottom: '12px', padding: '8px 12px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <span style={{ fontWeight: 500, color: 'var(--text)' }}>{trendSummary.startDate} → {trendSummary.endDate}</span>
                <span style={{ display: 'inline-block', width: '1px', height: '10px', background: 'var(--border)', margin: '0 8px', verticalAlign: 'middle' }} />
                Playcount grew <span style={{ color: 'var(--accent)', fontWeight: 500 }}>+{trendSummary.growth}%</span>
                <span style={{ display: 'inline-block', width: '1px', height: '10px', background: 'var(--border)', margin: '0 8px', verticalAlign: 'middle' }} />
                Rank <span style={{ fontWeight: 500 }}>#{trendSummary.firstRank} → #{trendSummary.lastRank}</span>
              </div>
            )}
          </>
        )}
        {!histLoading && history.length === 0 && (
          <div style={{ fontSize: '12px', color: 'var(--muted)', padding: '10px 12px', background: 'var(--bg)', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '12px' }}>
            Trend data not available for this track.
          </div>
        )}

        {/* Score history */}
        {scoreHistory.length > 0 && (
          <div style={{ background: 'var(--bg)', borderRadius: '8px', padding: '12px', border: '1px solid var(--border)', marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
              Score history
            </div>
            {scoreHistory.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < scoreHistory.length - 1 ? '6px' : 0 }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: 'var(--text)' }}>
                  {s.date} — <span style={{ color: 'var(--accent)', fontWeight: 500 }}>{s.score}%</span>
                </span>
              </div>
            ))}
            {scoreHistory.length === 1 && (
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '6px' }}>
                Score history grows as the track is flagged across multiple cooldown windows.
              </div>
            )}
          </div>
        )}

        {/* Signal breakdown */}
        {pct && (
          <div style={{ background: 'var(--bg)', borderRadius: '8px', padding: '12px', border: '1px solid var(--border)', marginBottom: '10px' }}>
            <div style={{ fontSize: '10px', color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '8px' }}>
              Signal breakdown
            </div>
            {(alert.models_fired === 'isolation_forest' || alert.models_fired === 'isolation_forest+kmeans') && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: '4px' }} />
                <div style={{ fontSize: '12px', color: 'var(--sub)', lineHeight: 1.5 }}>
                  Isolation Forest flagged this track — playcount spiked anomalously vs its own baseline history.
                </div>
              </div>
            )}
            {(alert.models_fired === 'kmeans' || alert.models_fired === 'isolation_forest+kmeans') && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: '4px' }} />
                <div style={{ fontSize: '12px', color: 'var(--sub)', lineHeight: 1.5 }}>
                  K-Means: cluster shifted since yesterday — track moved into a higher-engagement group overnight.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Last.fm link */}
        <a
          href={alert.url || 'https://last.fm'}
          target="_blank"
          rel="noreferrer"
          style={{ fontSize: '11px', color: 'var(--accent)', textDecoration: 'none' }}
        >
          View on Last.fm →
        </a>
      </div>
    </div>
  );
}